import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Clipboard, ClipboardCheck, ListCheck } from "lucide-react";

const ChecklistsUseCase = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="relative bg-[#0077bb] py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              <h1 className="text-4xl font-bold text-white mb-6">
                Digital Checklists & Safety Procedures
              </h1>
              <p className="text-xl text-green-100 max-w-3xl">
                Ensure compliance and safety with digital checklists that standardize processes and track completion in real-time.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
                alt="Digital checklist management"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Streamline Operations with Digital Checklists
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Replace paper checklists with digital forms that ensure consistency and compliance across your organization:
              </p>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Create standardized procedures and protocols
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Track completion rates and identify bottlenecks
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Ensure compliance with safety regulations
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Generate automated compliance reports
                </li>
              </ul>
            </div>
          </div>

          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-12">
                Key Features
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Clipboard className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Digital Forms</h3>
                  <p className="text-gray-600">
                    Create and customize digital checklists tailored to your specific operational needs.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <ClipboardCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Real-time Tracking</h3>
                  <p className="text-gray-600">
                    Monitor checklist completion in real-time and get instant notifications for critical issues.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <ListCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Compliance Reports</h3>
                  <p className="text-gray-600">
                    Generate comprehensive reports and analytics to ensure compliance and identify trends.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Common Use Cases Section */}
      <div className="w-full bg-gray-50">
        <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common Use Cases
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Safety Inspections</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Equipment safety checks</li>
                <li>Workplace hazard assessments</li>
                <li>Safety compliance audits</li>
                <li>Incident investigation forms</li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Quality Control</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Product quality inspections</li>
                <li>Process control checks</li>
                <li>Quality assurance audits</li>
                <li>Compliance documentation</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <section className="py-20">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">
                Ready to Transform Your Checklist Management?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Join organizations that have revolutionized their operations with our digital checklist solution.
              </p>
              <div className="space-x-4">
                <Link to="/request-demo">
                  <Button className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors">
                    Request Demo
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border border-green-600 text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ChecklistsUseCase;
